/**
 * Bulk Engraving Modal - Decade Awards
 *
 * <bulk-engraving-modal> Web Component.
 * Self-contained modal for entering multiple engravings and adding them all to cart.
 *
 * This component is fully isolated — it does NOT modify any external DOM
 * (no body classes, no cart drawer manipulation, no focus trap side effects).
 */

if (!customElements.get('bulk-engraving-modal')) {
  customElements.define('bulk-engraving-modal', class BulkEngravingModal extends HTMLElement {

    constructor() {
      super();
      this.quantity = 2;
      this.minQty = 2;
      this.maxQty = 50;
      this.rows = [];
      this.lockedLines = new Set();
      this.isOpen = false;
      this.variantId = null;
      this._saveTimer = null;
      this._rendered = false;
      this._unsubVariant = null;
      this._escHandler = null;
    }

    connectedCallback() {
      this.productTitle = this.dataset.productTitle || '';
      this.variantId = this.dataset.variantId;
      this.price = this.dataset.price || '';
      this.priceRaw = parseInt(this.dataset.priceRaw, 10) || 0;
      this.lineCount = parseInt(this.dataset.lineCount, 10) || 1;
      this.activeLineCount = this.lineCount;
      this.charsPerLine = parseInt(this.dataset.charsPerLine, 10) || 30;
      this.productUrl = this.dataset.productUrl || '';
      this.productId = this.dataset.productId || '';
      this.productImage = this.dataset.productImage || '';

      this.ribbonOptions = this._parseJSON(this.dataset.ribbonOptions);
      this.ribbonLabel = this.dataset.ribbonLabel || 'Select Ribbon';
      this.colorfillOptions = this._parseJSON(this.dataset.colorfillOptions);
      this.colorfillLabel = this.dataset.colorfillLabel || 'Text Colorfill';
      this.plateOptions = this._parseJSON(this.dataset.plateOptions);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubVariant = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (evt) => {
          if (evt?.data?.variant?.id) {
            this.variantId = String(evt.data.variant.id);
          }
        });
      }

      this._initRows(this.quantity);
    }

    disconnectedCallback() {
      if (this._unsubVariant) this._unsubVariant();
      if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
    }

    // Called by product-customizer.js when the selected variant changes engraving config
    updateConfig(lines, chars) {
      this.lineCount    = lines;
      this.charsPerLine = chars;

      // Always reset activeLineCount to the new variant's full line count
      this.activeLineCount = lines;
      this.rows = this.rows.map(row => {
        const clamped = row.slice(0, lines);
        while (clamped.length < lines) clamped.push('');
        return clamped;
      });

      if (!this._rendered) return; // not yet opened — nothing to update in DOM

      // 1. Update line count picker pills
      const picker = this.querySelector('[data-be-lines-picker]');
      if (lines > 1) {
        let pills = '';
        for (let i = 1; i <= lines; i++) {
          const active = i === this.activeLineCount;
          pills += `<button type="button" class="be-lines__pill${active ? ' be-lines__pill--active' : ''}" data-be-set-lines="${i}" aria-label="${i} engraving line${i > 1 ? 's' : ''}">${i}</button>`;
        }
        if (picker) {
          picker.querySelector('.be-lines__pills').innerHTML = pills;
          // No re-binding needed — clicks handled via delegation in _handleClick
        } else {
          // Picker didn't exist (was lineCount=1 before), insert before grid
          const grid = this.querySelector('[data-be-grid]');
          if (grid) grid.insertAdjacentHTML('beforebegin', this._renderLineCountPicker());
        }
      } else if (picker) {
        picker.remove();
      }

      // 2. Refresh grid header + all rows (uses this.activeLineCount + this.charsPerLine)
      this._refreshGridRows();

      // 3. Refresh mobile cards if visible
      this._refreshCards();
    }

    _parseJSON(str) {
      if (!str) return null;
      try { return JSON.parse(str); } catch { return null; }
    }

    _initRows(count) {
      this.rows = [];
      for (let i = 0; i < count; i++) {
        this.rows.push(new Array(this.lineCount).fill(''));
      }
    }

    // ----------------------------------------
    // OPEN / CLOSE — no external side effects
    // ----------------------------------------

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      // Move to document.body to escape any parent transforms/containment
      // that break position:fixed in Shopify themes
      this._originalParent = this.parentNode;
      this._originalNextSibling = this.nextSibling;
      document.body.appendChild(this);

      // Lock page scroll — compensate for scrollbar width to prevent layout shift
      this._scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      if (this._scrollbarWidth > 0) {
        document.body.style.paddingRight = this._scrollbarWidth + 'px';
      }

      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');

      if (!this._rendered) {
        this._render();
        this._rendered = true;
      }

      // Check for saved data
      const saved = this._loadFromLocalStorage();
      if (saved && saved.rows && saved.rows.length > 0) {
        this._showRestorePrompt(saved);
      }

      // Escape key handler (scoped to this instance)
      this._escHandler = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this._escHandler);

      // Focus the close button
      requestAnimationFrame(() => {
        this.querySelector('[data-be-close]')?.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }

      // Play closing animation, then clean up
      this.classList.add('be--closing');

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;

        this.classList.remove('be--closing');
        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');

        // Unlock page scroll
        document.documentElement.style.overflow = '';
        document.body.style.paddingRight = '';

        // Move back to original location in DOM
        if (this._originalParent) {
          if (this._originalNextSibling) {
            this._originalParent.insertBefore(this, this._originalNextSibling);
          } else {
            this._originalParent.appendChild(this);
          }
          this._originalParent = null;
          this._originalNextSibling = null;
        }

        // Return focus to trigger
        const trigger = document.getElementById('bulk-engraving-trigger');
        if (trigger) trigger.focus();
      };

      // Wait for the closing animation to finish (200ms matches CSS)
      const container = this.querySelector('.be-container');
      if (container) {
        container.addEventListener('animationend', cleanup, { once: true });
        // Fallback in case animationend doesn't fire
        setTimeout(cleanup, 250);
      } else {
        cleanup();
      }
    }

    // ----------------------------------------
    // RENDER
    // ----------------------------------------

    _render() {
      this.innerHTML = `
        <div class="be-backdrop" data-be-backdrop></div>
        <div class="be-container">
          ${this._renderHeader()}
          <div class="be-body" data-be-body>
            <div data-be-messages></div>
            ${this._renderSharedOptions()}
            ${this._renderQuantityPicker()}
            ${this.lineCount > 1 ? this._renderLineCountPicker() : ''}
            ${this._renderGrid()}
            ${this._renderCards()}
            <button type="button" class="be-add-row" data-be-add-row>+ Add Row</button>
          </div>
          ${this._renderFooter()}
        </div>
      `;

      this._bindEvents();
    }

    _renderHeader() {
      const imgHTML = this.productImage
        ? `<img class="be-header__image" src="${this._esc(this.productImage)}" alt="${this._esc(this.productTitle)}" width="48" height="48" loading="eager">`
        : '';
      return `
        <div class="be-header">
          <div class="be-header__left">
            ${imgHTML}
            <div class="be-header__info">
              <h2 class="be-header__title">${this._esc(this.productTitle)}</h2>
              <span class="be-header__price">${this._esc(this.price)} each</span>
            </div>
          </div>
          <button type="button" class="be-close" data-be-close aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>
      `;
    }

    _renderSharedOptions() {
      const hasAny = this.ribbonOptions || this.colorfillOptions || this.plateOptions;
      if (!hasAny) return '';

      let fields = '';

      if (this.ribbonOptions) {
        const opts = this.ribbonOptions.map(r => `<option value="${this._esc(r)}">${this._esc(r)}</option>`).join('');
        fields += `
          <div class="be-shared__field">
            <label class="be-shared__label">${this._esc(this.ribbonLabel)} <span style="color:var(--da-red,#9a282c)">*</span></label>
            <select class="be-shared__select" data-be-shared="ribbon">
              <option value="">Choose a ribbon...</option>
              ${opts}
            </select>
          </div>
        `;
      }

      if (this.colorfillOptions) {
        const radios = this.colorfillOptions.map((c, i) => `
          <label class="be-shared__radio-label">
            <input type="radio" name="be-colorfill" value="${this._esc(c)}" ${i === 0 ? 'checked' : ''}>
            <span>${this._esc(c)}</span>
          </label>
        `).join('');
        fields += `
          <div class="be-shared__field">
            <span class="be-shared__label">${this._esc(this.colorfillLabel)}</span>
            <div class="be-shared__radio-group">${radios}</div>
          </div>
        `;
      }

      if (this.plateOptions) {
        const opts = this.plateOptions.map(p => `<option value="${this._esc(p)}">${this._esc(p)}</option>`).join('');
        fields += `
          <div class="be-shared__field">
            <label class="be-shared__label"># of Perpetual Plates</label>
            <select class="be-shared__select" data-be-shared="plates">
              ${opts}
            </select>
          </div>
        `;
      }

      return `
        <div class="be-shared" data-be-shared-panel>
          <button type="button" class="be-shared__toggle" data-be-shared-toggle>
            Shared Options (applied to all awards)
            <svg class="be-shared__toggle-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="m6 9 6 6 6-6"/></svg>
          </button>
          <div class="be-shared__content">${fields}</div>
        </div>
      `;
    }

    _renderQuantityPicker() {
      return `
        <div class="be-quantity">
          <span class="be-quantity__label">How many awards?</span>
          <div class="be-quantity__controls">
            <button type="button" class="be-quantity__btn" data-be-qty-minus aria-label="Decrease quantity">&minus;</button>
            <input type="number" class="be-quantity__value" data-be-qty-value value="${this.quantity}" min="${this.minQty}" max="${this.maxQty}" aria-label="Number of awards">
            <button type="button" class="be-quantity__btn" data-be-qty-plus aria-label="Increase quantity">+</button>
          </div>
        </div>
      `;
    }

    _renderLineCountPicker() {
      let pills = '';
      for (let i = 1; i <= this.lineCount; i++) {
        const active = i === this.activeLineCount;
        pills += `<button type="button" class="be-lines__pill${active ? ' be-lines__pill--active' : ''}" data-be-set-lines="${i}" aria-label="${i} engraving line${i > 1 ? 's' : ''}">${i}</button>`;
      }
      return `
        <div class="be-lines-picker" data-be-lines-picker>
          <span class="be-lines__label">Lines per award</span>
          <div class="be-lines__pills">${pills}</div>
        </div>
      `;
    }

    setActiveLineCount(n) {
      n = Math.max(1, Math.min(this.lineCount, n));
      if (n === this.activeLineCount) return;

      // Unlock any locked lines that are beyond the new count
      for (let l = n; l < this.lineCount; l++) {
        this.lockedLines.delete(l);
      }

      this.activeLineCount = n;

      // Update pill buttons
      this.querySelectorAll('[data-be-set-lines]').forEach(btn => {
        const val = parseInt(btn.dataset.beSetLines, 10);
        btn.classList.toggle('be-lines__pill--active', val === n);
      });

      this._refreshGridRows();
      this._refreshCards();
      this._updateApplyToAllButtons();
      this._scheduleSave();
    }

    _renderGrid() {
      const cols = `40px repeat(${this.activeLineCount}, 1fr) 80px`;

      let headerCells = '<span class="be-grid__header-cell be-grid__header-cell--num">#</span>';
      for (let l = 0; l < this.activeLineCount; l++) {
        const locked = this.lockedLines.has(l);
        const value = this._getFirstValueForLine(l);
        if (locked) {
          headerCells += `<span class="be-grid__header-cell">Line ${l + 1} <button type="button" class="be-header-line-btn be-header-line-btn--locked" data-be-unlock-line="${l}" title="Unlock Line ${l + 1}">Unlock</button></span>`;
        } else {
          headerCells += `<span class="be-grid__header-cell">Line ${l + 1} <button type="button" class="be-header-line-btn" data-be-apply-line="${l}" title="Apply Line ${l + 1} to all awards" ${!value ? 'disabled' : ''}>Apply to all</button></span>`;
        }
      }
      headerCells += '<span class="be-grid__header-cell be-grid__header-cell--actions"></span>';

      let dataRows = '';
      for (let r = 0; r < this.quantity; r++) {
        dataRows += this._renderGridRow(r);
      }

      return `
        <div class="be-grid" data-be-grid>
          <div class="be-grid__header" style="grid-template-columns: ${cols}">
            ${headerCells}
          </div>
          <div data-be-grid-rows>
            ${dataRows}
          </div>
        </div>
      `;
    }

    _renderGridRow(rowIndex) {
      const cols = `40px repeat(${this.activeLineCount}, 1fr) 80px`;
      let cells = `<span class="be-grid__row-num">${rowIndex + 1}</span>`;

      for (let l = 0; l < this.activeLineCount; l++) {
        const val = this.rows[rowIndex] ? (this.rows[rowIndex][l] || '') : '';
        const lineLocked = this.lockedLines.has(l);
        cells += `
          <div class="be-grid__input-wrap">
            <input type="text"
              class="be-grid__input"
              data-be-input data-row="${rowIndex}" data-line="${l}"
              maxlength="${this.charsPerLine}"
              placeholder="Line ${l + 1}"
              value="${this._esc(val)}"
              autocomplete="off"
              ${lineLocked ? 'disabled' : ''}
            >
            <span class="be-grid__char-count" data-be-charcount data-row="${rowIndex}" data-line="${l}">${val.length}/${this.charsPerLine}</span>
          </div>
        `;
      }

      cells += `
        <div class="be-grid__row-actions">
          <button type="button" class="be-grid__delete" data-be-delete-row="${rowIndex}" title="Remove row ${rowIndex + 1}" aria-label="Remove row ${rowIndex + 1}">
            Delete
          </button>
        </div>
      `;

      return `<div class="be-grid__row" data-be-row="${rowIndex}" style="grid-template-columns: ${cols}">${cells}</div>`;
    }

    _renderLineControls() {
      let html = '<div class="be-line-controls" data-be-line-controls>';
      for (let l = 0; l < this.activeLineCount; l++) {
        const locked = this.lockedLines.has(l);
        const value = this._getFirstValueForLine(l);
        if (locked) {
          html += `
            <div class="be-line-controls__item be-line-controls__item--locked">
              <span class="be-line-controls__label">Line ${l + 1}</span>
              <span class="be-line-controls__value">${this._esc(value)}</span>
              <button type="button" class="be-line-controls__btn be-line-controls__btn--unlock" data-be-unlock-line="${l}">Unlock</button>
            </div>`;
        } else {
          html += `
            <div class="be-line-controls__item">
              <span class="be-line-controls__label">Line ${l + 1}</span>
              <button type="button" class="be-line-controls__btn" data-be-apply-line="${l}" ${!value ? 'disabled' : ''}>Apply to all</button>
            </div>`;
        }
      }
      html += '</div>';
      return html;
    }

    _renderCards() {
      let cards = '';
      for (let r = 0; r < this.quantity; r++) {
        cards += this._renderCard(r);
      }
      return `<div data-be-mobile-section>${this._renderLineControls()}<div class="be-cards" data-be-cards>${cards}</div></div>`;
    }

    _renderCard(rowIndex) {
      let fields = '';
      for (let l = 0; l < this.activeLineCount; l++) {
        const val = this.rows[rowIndex] ? (this.rows[rowIndex][l] || '') : '';
        const lineLocked = this.lockedLines.has(l);
        fields += `
          <div>
            <div class="be-card__field-label">Line ${l + 1}${lineLocked ? ' <span class="be-card__locked-badge">Locked</span>' : ''}</div>
            <div class="be-card__input-wrap">
              <input type="text"
                class="be-card__input"
                data-be-input data-row="${rowIndex}" data-line="${l}"
                maxlength="${this.charsPerLine}"
                placeholder="Line ${l + 1}"
                value="${this._esc(val)}"
                autocomplete="off"
                ${lineLocked ? 'disabled' : ''}
              >
              <span class="be-card__char-count" data-be-charcount data-row="${rowIndex}" data-line="${l}">${val.length}/${this.charsPerLine}</span>
            </div>
          </div>
        `;
      }

      return `
        <div class="be-card" data-be-card="${rowIndex}">
          <div class="be-card__header">
            <span class="be-card__title">Award #${rowIndex + 1}</span>
            <div class="be-card__actions">
              <button type="button" class="be-card__delete" data-be-delete-row="${rowIndex}">Delete</button>
            </div>
          </div>
          <div class="be-card__fields">${fields}</div>
        </div>
      `;
    }

    _renderFooter() {
      return `
        <div class="be-footer">
          <div class="be-footer__summary" data-be-summary>
            <strong>${this.quantity} awards</strong> &times; ${this._esc(this.price)}
          </div>
          <button type="button" class="be-footer__cta" data-be-submit>
            Review ${this.quantity} Awards
          </button>
        </div>
      `;
    }

    // ----------------------------------------
    // EVENT BINDING
    // ----------------------------------------

    _bindEvents() {
      // Close when clicking outside the modal panel (backdrop or any area outside .be-container)
      this.addEventListener('click', (e) => {
        if (!e.target.closest('.be-container')) {
          this.close();
        }
      });
      this.querySelector('[data-be-close]')?.addEventListener('click', () => this.close());

      this.querySelector('[data-be-shared-toggle]')?.addEventListener('click', () => {
        const panel = this.querySelector('[data-be-shared-panel]');
        if (panel) panel.toggleAttribute('open');
      });

      this.querySelector('[data-be-qty-minus]')?.addEventListener('click', () => this.setQuantity(this.quantity - 1));
      this.querySelector('[data-be-qty-plus]')?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
      this.querySelector('[data-be-qty-value]')?.addEventListener('change', (e) => {
        const v = parseInt(e.target.value, 10);
        if (!isNaN(v)) this.setQuantity(v);
      });

      this.querySelector('[data-be-add-row]')?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
      // [data-be-set-lines] pill clicks handled via delegation in _handleClick
      this.querySelector('[data-be-submit]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._showReview();
      });

      this.addEventListener('input', (e) => this._handleInput(e));
      this.addEventListener('paste', (e) => this._handlePaste(e));
      this.addEventListener('click', (e) => this._handleClick(e));
    }

    _handleInput(e) {
      const target = e.target;

      if (target.matches('[data-be-input]')) {
        const row = parseInt(target.dataset.row, 10);
        const line = parseInt(target.dataset.line, 10);
        if (this.rows[row]) {
          this.rows[row][line] = target.value;
        }
        this._updateCharCount(row, line, target.value.length);
        this._updateApplyToAllButtons();
        this._scheduleSave();
        return;
      }

      if (target.matches('[data-be-qty-value]')) {
        const v = parseInt(target.value, 10);
        if (!isNaN(v)) this.setQuantity(v);
      }
    }

    _handlePaste(e) {
      const target = e.target;
      if (!target.matches('[data-be-input]')) return;
      const max = parseInt(target.maxLength, 10);
      if (!max) return;

      setTimeout(() => {
        if (target.value.length > max) {
          target.value = target.value.substring(0, max);
          target.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 0);
    }

    _handleClick(e) {
      // Line count pill — delegated so dynamically added pills work
      const setLinesBtn = e.target.closest('[data-be-set-lines]');
      if (setLinesBtn) {
        const n = parseInt(setLinesBtn.dataset.beSetLines, 10);
        if (!isNaN(n)) this.setActiveLineCount(n);
        return;
      }

      const deleteBtn = e.target.closest('[data-be-delete-row]');
      if (deleteBtn) {
        const rowIndex = parseInt(deleteBtn.dataset.beDeleteRow, 10);
        this._deleteRow(rowIndex);
        return;
      }

      const unlockBtn = e.target.closest('[data-be-unlock-line]');
      if (unlockBtn) {
        const lineIndex = parseInt(unlockBtn.dataset.beUnlockLine, 10);
        this._unlockLine(lineIndex);
        return;
      }

      const applyBtn = e.target.closest('[data-be-apply-line]');
      if (applyBtn && !applyBtn.disabled) {
        const lineIndex = parseInt(applyBtn.dataset.beApplyLine, 10);
        this._applyLineToAll(lineIndex);
        return;
      }
    }

    // ----------------------------------------
    // QUANTITY MANAGEMENT
    // ----------------------------------------

    setQuantity(n) {
      n = Math.max(this.minQty, Math.min(this.maxQty, n));
      if (n === this.quantity) return;

      const old = this.quantity;
      this.quantity = n;

      if (n > old) {
        for (let i = old; i < n; i++) {
          const newRow = new Array(this.lineCount).fill('');
          this.lockedLines.forEach(l => {
            const val = this._getFirstValueForLine(l);
            if (val) newRow[l] = val;
          });
          this.rows.push(newRow);
        }
      } else {
        this.rows.length = n;
      }

      const qtyInput = this.querySelector('[data-be-qty-value]');
      if (qtyInput) qtyInput.value = n;

      const minus = this.querySelector('[data-be-qty-minus]');
      const plus = this.querySelector('[data-be-qty-plus]');
      if (minus) minus.disabled = n <= this.minQty;
      if (plus) plus.disabled = n >= this.maxQty;

      this._refreshGridRows();
      this._refreshCards();
      this._updateApplyToAllButtons();
      this._updateFooter();
      this._scheduleSave();
    }

    _deleteRow(rowIndex) {
      if (this.quantity <= this.minQty) return;
      this.rows.splice(rowIndex, 1);
      this.quantity = this.rows.length;

      const qtyInput = this.querySelector('[data-be-qty-value]');
      if (qtyInput) qtyInput.value = this.quantity;

      const minus = this.querySelector('[data-be-qty-minus]');
      if (minus) minus.disabled = this.quantity <= this.minQty;

      this._refreshGridRows();
      this._refreshCards();
      this._updateApplyToAllButtons();
      this._updateFooter();
      this._scheduleSave();
    }

    _refreshGridRows() {
      const grid = this.querySelector('[data-be-grid]');
      if (!grid) return;
      const cols = `40px repeat(${this.activeLineCount}, 1fr) 80px`;

      let headerCells = '<span class="be-grid__header-cell be-grid__header-cell--num">#</span>';
      for (let l = 0; l < this.activeLineCount; l++) {
        const locked = this.lockedLines.has(l);
        const value = this._getFirstValueForLine(l);
        if (locked) {
          headerCells += `<span class="be-grid__header-cell">Line ${l + 1} <button type="button" class="be-header-line-btn be-header-line-btn--locked" data-be-unlock-line="${l}" title="Unlock Line ${l + 1}">Unlock</button></span>`;
        } else {
          headerCells += `<span class="be-grid__header-cell">Line ${l + 1} <button type="button" class="be-header-line-btn" data-be-apply-line="${l}" title="Apply Line ${l + 1} to all awards" ${!value ? 'disabled' : ''}>Apply to all</button></span>`;
        }
      }
      headerCells += '<span class="be-grid__header-cell be-grid__header-cell--actions"></span>';

      let dataRows = '';
      for (let r = 0; r < this.quantity; r++) {
        dataRows += this._renderGridRow(r);
      }

      grid.innerHTML = `
        <div class="be-grid__header" style="grid-template-columns: ${cols}">${headerCells}</div>
        <div data-be-grid-rows>${dataRows}</div>
      `;
    }

    _refreshCards() {
      const section = this.querySelector('[data-be-mobile-section]');
      if (!section) return;
      let cards = '';
      for (let r = 0; r < this.quantity; r++) {
        cards += this._renderCard(r);
      }
      section.innerHTML = `${this._renderLineControls()}<div class="be-cards" data-be-cards>${cards}</div>`;
    }

    _updateFooter() {
      const summary = this.querySelector('[data-be-summary]');
      if (summary) {
        summary.innerHTML = `<strong>${this.quantity} awards</strong> &times; ${this._esc(this.price)}`;
      }
      const cta = this.querySelector('[data-be-submit]');
      if (cta) cta.textContent = `Review ${this.quantity} Awards`;
    }

    _updateCharCount(row, line, len) {
      this.querySelectorAll(`[data-be-charcount][data-row="${row}"][data-line="${line}"]`).forEach(cc => {
        cc.textContent = `${len}/${this.charsPerLine}`;
        cc.classList.toggle('be-grid__char-count--warning', len >= this.charsPerLine * 0.9);
        cc.classList.toggle('be-grid__char-count--limit', len >= this.charsPerLine);
      });
    }

    /**
     * Gets the first non-empty value for a specific line number across all rows.
     */
    _getFirstValueForLine(lineIndex) {
      for (let r = 0; r < this.rows.length; r++) {
        const val = (this.rows[r]?.[lineIndex] || '').trim();
        if (val) return val;
      }
      return '';
    }

    /**
     * Copies the first non-empty value for the given line to ALL rows for that line,
     * then locks that line (disables inputs across all rows for that line number).
     */
    _applyLineToAll(lineIndex) {
      const value = this._getFirstValueForLine(lineIndex);
      if (!value) return;
      const clamped = value.length > this.charsPerLine ? value.slice(0, this.charsPerLine) : value;
      for (let r = 0; r < this.rows.length; r++) {
        if (this.rows[r]) this.rows[r][lineIndex] = clamped;
      }
      this.lockedLines.add(lineIndex);
      this._refreshGridRows();
      this._refreshCards();
      this._scheduleSave();
    }

    _unlockLine(lineIndex) {
      this.lockedLines.delete(lineIndex);
      this._refreshGridRows();
      this._refreshCards();
      this._scheduleSave();
    }

    _updateApplyToAllButtons() {
      this.querySelectorAll('[data-be-apply-line]').forEach(btn => {
        const lineIndex = parseInt(btn.dataset.beApplyLine, 10);
        const value = this._getFirstValueForLine(lineIndex);
        btn.disabled = !value;
      });
    }

    // ----------------------------------------
    // REVIEW / SUMMARY STEP
    // ----------------------------------------

    _showReview() {
      if (this.ribbonOptions) {
        const ribbonSelect = this.querySelector('[data-be-shared="ribbon"]');
        if (ribbonSelect && !ribbonSelect.value) {
          this._showError('Please select a ribbon option.');
          ribbonSelect.focus();
          return;
        }
      }

      this._pendingItems = [];
      let skippedCount = 0;

      for (let r = 0; r < this.rows.length; r++) {
        const row = this.rows[r];
        const activeSlice = row.slice(0, this.activeLineCount);
        const hasContent = activeSlice.some(v => v && v.trim() !== '');
        if (!hasContent) { skippedCount++; continue; }

        for (let l = 0; l < this.activeLineCount; l++) {
          if (row[l] && row[l].length > this.charsPerLine) {
            this._showError(`Row ${r + 1}, Line ${l + 1} exceeds the ${this.charsPerLine} character limit.`);
            return;
          }
        }

        const properties = {};
        for (let l = 0; l < this.activeLineCount; l++) {
          properties[`Line ${l + 1}`] = row[l] || '';
        }

        const ribbonSelect = this.querySelector('[data-be-shared="ribbon"]');
        if (ribbonSelect && ribbonSelect.value) properties[this.ribbonLabel] = ribbonSelect.value;

        const colorfillChecked = this.querySelector('input[name="be-colorfill"]:checked');
        if (colorfillChecked) properties[this.colorfillLabel] = colorfillChecked.value;

        const platesSelect = this.querySelector('[data-be-shared="plates"]');
        if (platesSelect && platesSelect.value) properties['# of Perpetual Plates'] = platesSelect.value;

        this._pendingItems.push({
          id: parseInt(this.variantId, 10),
          quantity: 1,
          properties,
          _line1: row[0] || '',
          _otherLines: row.slice(1, this.activeLineCount)
        });
      }

      if (this._pendingItems.length === 0) {
        this._showError('Please fill in at least one row with engraving text.');
        return;
      }

      this._pendingItems.forEach((item, i) => {
        item.properties['_Item'] = `${i + 1} of ${this._pendingItems.length}`;
        item.properties['_Bulk Order'] = `${this._pendingItems.length} items`;
      });

      if (skippedCount > 0) {
        if (!confirm(`${skippedCount} empty row(s) will be skipped. Review ${this._pendingItems.length} award(s)?`)) return;
      }

      this._checkedItems = new Set();
      const body = this.querySelector('[data-be-body]');
      const footer = this.querySelector('.be-footer');

      this._editBodyHTML = body.innerHTML;

      let sharedSummary = '';
      const ribbonVal = this.querySelector('[data-be-shared="ribbon"]')?.value;
      const colorfillVal = this.querySelector('input[name="be-colorfill"]:checked')?.value;
      const platesVal = this.querySelector('[data-be-shared="plates"]')?.value;
      if (ribbonVal) sharedSummary += `<span class="be-review__shared-tag">${this._esc(this.ribbonLabel)}: ${this._esc(ribbonVal)}</span>`;
      if (colorfillVal) sharedSummary += `<span class="be-review__shared-tag">${this._esc(this.colorfillLabel)}: ${this._esc(colorfillVal)}</span>`;
      if (platesVal) sharedSummary += `<span class="be-review__shared-tag">Plates: ${this._esc(platesVal)}</span>`;

      let itemsHTML = '';
      this._pendingItems.forEach((item, i) => {
        const otherLines = item._otherLines.filter(v => v && v.trim());
        const hasOtherLines = otherLines.length > 0;

        let otherLinesHTML = '';
        if (hasOtherLines) {
          const linesDetail = item._otherLines.map((v, li) =>
            `<div class="be-review__detail-line"><span class="be-review__detail-label">Line ${li + 2}:</span> ${this._esc(v || '(empty)')}</div>`
          ).join('');

          otherLinesHTML = `
            <button type="button" class="be-review__accordion" data-be-review-toggle="${i}">
              ${otherLines.length} more line${otherLines.length > 1 ? 's' : ''}
              <svg class="be-review__accordion-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div class="be-review__details" data-be-review-details="${i}" hidden>
              ${linesDetail}
            </div>
          `;
        }

        itemsHTML += `
          <div class="be-review__item" data-be-review-item="${i}">
            <label class="be-review__check-label">
              <input type="checkbox" class="be-review__checkbox" data-be-review-check="${i}">
              <span class="be-review__check-custom"></span>
            </label>
            <div class="be-review__content">
              <div class="be-review__header">
                <span class="be-review__num">#${i + 1}</span>
                <span class="be-review__line1">${this._esc(item._line1 || '(empty)')}</span>
              </div>
              ${otherLinesHTML}
            </div>
          </div>
        `;
      });

      body.innerHTML = `
        <div class="be-review" data-be-review>
          <div class="be-review__intro">
            <h3 class="be-review__title">Review Your Order</h3>
            <p class="be-review__subtitle">Check each item to confirm the engraving is correct.</p>
          </div>
          ${sharedSummary ? `<div class="be-review__shared">${sharedSummary}</div>` : ''}
          <div class="be-review__select-all">
            <label class="be-review__check-label">
              <input type="checkbox" class="be-review__checkbox" data-be-review-check-all>
              <span class="be-review__check-custom"></span>
            </label>
            <span class="be-review__select-all-text">Select All</span>
          </div>
          <div class="be-review__items">${itemsHTML}</div>
        </div>
      `;

      footer.innerHTML = `
        <button type="button" class="be-footer__back" data-be-review-back>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          Back
        </button>
        <span class="be-footer__review-count" data-be-review-counter>0 of ${this._pendingItems.length} confirmed</span>
        <button type="button" class="be-footer__cta" data-be-review-submit disabled>
          Add ${this._pendingItems.length} to Cart
        </button>
      `;

      this._bindReviewEvents();
    }

    _bindReviewEvents() {
      this.querySelector('[data-be-review-back]')?.addEventListener('click', () => this._exitReview());

      this.querySelectorAll('[data-be-review-check]').forEach(cb => {
        cb.addEventListener('change', (e) => {
          const idx = parseInt(e.target.dataset.beReviewCheck, 10);
          if (e.target.checked) {
            this._checkedItems.add(idx);
          } else {
            this._checkedItems.delete(idx);
          }
          const item = this.querySelector(`[data-be-review-item="${idx}"]`);
          if (item) item.classList.toggle('be-review__item--checked', e.target.checked);
          this._updateReviewCounter();
        });
      });

      this.querySelector('[data-be-review-check-all]')?.addEventListener('change', (e) => {
        const checked = e.target.checked;
        this.querySelectorAll('[data-be-review-check]').forEach(cb => {
          cb.checked = checked;
          const idx = parseInt(cb.dataset.beReviewCheck, 10);
          if (checked) this._checkedItems.add(idx); else this._checkedItems.delete(idx);
          const item = this.querySelector(`[data-be-review-item="${idx}"]`);
          if (item) item.classList.toggle('be-review__item--checked', checked);
        });
        this._updateReviewCounter();
      });

      this.querySelectorAll('[data-be-review-toggle]').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = btn.dataset.beReviewToggle;
          const details = this.querySelector(`[data-be-review-details="${idx}"]`);
          if (details) {
            const isOpen = !details.hidden;
            details.hidden = isOpen;
            btn.classList.toggle('be-review__accordion--open', !isOpen);
          }
        });
      });

      this.querySelector('[data-be-review-submit]')?.addEventListener('click', () => this._submitCart());
    }

    _updateReviewCounter() {
      const counter = this.querySelector('[data-be-review-counter]');
      const total = this._pendingItems.length;
      const checked = this._checkedItems.size;
      if (counter) counter.textContent = `${checked} of ${total} confirmed`;

      const submitBtn = this.querySelector('[data-be-review-submit]');
      if (submitBtn) submitBtn.disabled = checked < total;

      const selectAll = this.querySelector('[data-be-review-check-all]');
      if (selectAll) {
        selectAll.checked = checked === total;
        selectAll.indeterminate = checked > 0 && checked < total;
      }
    }

    _exitReview() {
      const body = this.querySelector('[data-be-body]');
      if (body && this._editBodyHTML) {
        body.innerHTML = this._editBodyHTML;
      }

      this.querySelector('[data-be-shared-toggle]')?.addEventListener('click', () => {
        const panel = this.querySelector('[data-be-shared-panel]');
        if (panel) panel.toggleAttribute('open');
      });
      this.querySelector('[data-be-qty-minus]')?.addEventListener('click', () => this.setQuantity(this.quantity - 1));
      this.querySelector('[data-be-qty-plus]')?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
      this.querySelector('[data-be-qty-value]')?.addEventListener('change', (e) => {
        const v = parseInt(e.target.value, 10);
        if (!isNaN(v)) this.setQuantity(v);
      });
      this.querySelector('[data-be-add-row]')?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
      this.querySelectorAll('[data-be-set-lines]').forEach(btn => {
        btn.addEventListener('click', () => {
          const n = parseInt(btn.dataset.beSetLines, 10);
          if (!isNaN(n)) this.setActiveLineCount(n);
        });
      });

      const footer = this.querySelector('.be-footer');
      if (footer) {
        footer.innerHTML = `
          <div class="be-footer__summary" data-be-summary>
            <strong>${this.quantity} awards</strong> &times; ${this._esc(this.price)}
          </div>
          <button type="button" class="be-footer__cta" data-be-submit>
            Review ${this.quantity} Awards
          </button>
        `;
        this.querySelector('[data-be-submit]')?.addEventListener('click', () => this._showReview());
      }
    }

    // ----------------------------------------
    // CART — fully isolated, no DOM manipulation
    // ----------------------------------------

    async _submitCart() {
      if (!this._pendingItems || this._pendingItems.length === 0) return;

      const items = this._pendingItems.map(item => ({
        id: item.id,
        quantity: item.quantity,
        properties: item.properties
      }));

      const cta = this.querySelector('[data-be-review-submit]');
      if (cta) {
        cta.disabled = true;
        cta.classList.add('be-footer__cta--loading');
        cta.textContent = 'Adding to cart...';
      }

      this._clearMessages();

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || `Error ${response.status}`);
        }

        await response.json();

        // Clear saved data
        this._clearLocalStorage();

        // Publish cart update event for any theme listeners
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bulk-engraving' });
        }

        // Update cart icon count by fetching fresh cart data
        this._updateCartBubble();

        // Show success, then redirect to cart page
        this._showSuccess(`${items.length} award${items.length > 1 ? 's' : ''} added to cart!`);

        setTimeout(() => {
          this.close();
          this._resetForm();
          // Navigate to cart — cleanest approach, avoids all DOM manipulation
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }, 1000);

      } catch (err) {
        this._showError(err.message || 'Something went wrong. Please try again.');
        if (cta) {
          cta.disabled = false;
          cta.classList.remove('be-footer__cta--loading');
          cta.textContent = `Add ${this._pendingItems.length} to Cart`;
        }
      }
    }

    async _updateCartBubble() {
      // Fetch the current cart to get item count, then update the bubble
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) return;
        const cart = await res.json();
        const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (bubble) bubble.textContent = cart.item_count;
      } catch { /* non-critical, page will redirect anyway */ }
    }

    _resetForm() {
      this.quantity = 2;
      this.activeLineCount = this.lineCount;
      this.lockedLines.clear();
      this._initRows(this.quantity);
      this._rendered = false;
    }

    // ----------------------------------------
    // MESSAGES
    // ----------------------------------------

    _showError(msg) {
      const el = this.querySelector('[data-be-messages]');
      if (el) el.innerHTML = `<div class="be-error">${this._esc(msg)}</div>`;
      // Scroll to top of body within modal
      const body = this.querySelector('[data-be-body]');
      if (body) body.scrollTop = 0;
    }

    _showSuccess(msg) {
      const el = this.querySelector('[data-be-messages]');
      if (el) el.innerHTML = `<div class="be-success">${this._esc(msg)}</div>`;
      const body = this.querySelector('[data-be-body]');
      if (body) body.scrollTop = 0;
    }

    _clearMessages() {
      const el = this.querySelector('[data-be-messages]');
      if (el) el.innerHTML = '';
    }

    // ----------------------------------------
    // LOCALSTORAGE PERSISTENCE
    // ----------------------------------------

    _storageKey() {
      return `be_${this.productId}`;
    }

    _scheduleSave() {
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this._saveToLocalStorage(), 500);
    }

    _saveToLocalStorage() {
      try {
        const data = {
          rows: this.rows,
          quantity: this.quantity,
          activeLineCount: this.activeLineCount,
          ribbon: this.querySelector('[data-be-shared="ribbon"]')?.value || '',
          colorfill: this.querySelector('input[name="be-colorfill"]:checked')?.value || '',
          plates: this.querySelector('[data-be-shared="plates"]')?.value || '',
          savedAt: Date.now()
        };
        localStorage.setItem(this._storageKey(), JSON.stringify(data));
      } catch { /* localStorage full or unavailable */ }
    }

    _loadFromLocalStorage() {
      try {
        const raw = localStorage.getItem(this._storageKey());
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - data.savedAt > 7 * 24 * 60 * 60 * 1000) {
          localStorage.removeItem(this._storageKey());
          return null;
        }
        return data;
      } catch { return null; }
    }

    _clearLocalStorage() {
      try { localStorage.removeItem(this._storageKey()); } catch { /* ignore */ }
    }

    _showRestorePrompt(saved) {
      const el = this.querySelector('[data-be-messages]');
      if (!el) return;

      const filledRows = saved.rows.filter(r => r.some(v => v && v.trim())).length;
      if (filledRows === 0) return;

      el.innerHTML = `
        <div class="be-restore">
          <span class="be-restore__text">Resume saved order (${filledRows} item${filledRows > 1 ? 's' : ''} with engravings)?</span>
          <div class="be-restore__actions">
            <button type="button" class="be-restore__btn be-restore__btn--resume" data-be-restore="resume">Resume</button>
            <button type="button" class="be-restore__btn be-restore__btn--fresh" data-be-restore="fresh">Start Fresh</button>
          </div>
        </div>
      `;

      el.querySelector('[data-be-restore="resume"]')?.addEventListener('click', () => {
        this._restoreFromSaved(saved);
        el.innerHTML = '';
      });

      el.querySelector('[data-be-restore="fresh"]')?.addEventListener('click', () => {
        this._clearLocalStorage();
        el.innerHTML = '';
      });
    }

    _restoreFromSaved(saved) {
      this.rows = saved.rows;
      this.quantity = saved.quantity || saved.rows.length;
      if (saved.activeLineCount && saved.activeLineCount >= 1 && saved.activeLineCount <= this.lineCount) {
        this.activeLineCount = saved.activeLineCount;
      }

      const qtyInput = this.querySelector('[data-be-qty-value]');
      if (qtyInput) qtyInput.value = this.quantity;
      const minus = this.querySelector('[data-be-qty-minus]');
      if (minus) minus.disabled = this.quantity <= this.minQty;

      // Update line count pills
      this.querySelectorAll('[data-be-set-lines]').forEach(btn => {
        const val = parseInt(btn.dataset.beSetLines, 10);
        btn.classList.toggle('be-lines__pill--active', val === this.activeLineCount);
      });

      this._refreshGridRows();
      this._refreshCards();
      this._updateApplyToAllButtons();
      this._updateFooter();

      if (saved.ribbon) {
        const ribbonSelect = this.querySelector('[data-be-shared="ribbon"]');
        if (ribbonSelect) ribbonSelect.value = saved.ribbon;
      }
      if (saved.colorfill) {
        const radio = this.querySelector(`input[name="be-colorfill"][value="${CSS.escape(saved.colorfill)}"]`);
        if (radio) radio.checked = true;
      }
      if (saved.plates) {
        const platesSelect = this.querySelector('[data-be-shared="plates"]');
        if (platesSelect) platesSelect.value = saved.plates;
      }
    }

    // ----------------------------------------
    // LOAD FROM FILE UPLOAD
    // ----------------------------------------

    /**
     * Accepts parsed rows from a file upload and populates the editor.
     * @param {string[][]} data - Array of rows, each row is an array of line values.
     */
    loadFromUpload(data) {
      if (!data || data.length === 0) return;

      // Ensure modal is rendered
      if (!this._rendered) {
        this.open();
      }

      // Build rows, clamping to lineCount and charsPerLine
      this.rows = data.map(row => {
        const r = new Array(this.lineCount).fill('');
        for (let i = 0; i < Math.min(row.length, this.lineCount); i++) {
          r[i] = (row[i] || '').trim().substring(0, this.charsPerLine);
        }
        return r;
      });

      this.quantity = Math.max(this.minQty, Math.min(this.maxQty, this.rows.length));
      this.rows.length = this.quantity; // trim if over maxQty

      // Update all UI
      const qtyInput = this.querySelector('[data-be-qty-value]');
      if (qtyInput) qtyInput.value = this.quantity;

      const minus = this.querySelector('[data-be-qty-minus]');
      const plus  = this.querySelector('[data-be-qty-plus]');
      if (minus) minus.disabled = this.quantity <= this.minQty;
      if (plus)  plus.disabled  = this.quantity >= this.maxQty;

      this._refreshGridRows();
      this._refreshCards();
      this._updateApplyToAllButtons();
      this._updateFooter();
      this._scheduleSave();
    }

    // ----------------------------------------
    // HELPERS
    // ----------------------------------------

    _esc(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = String(str);
      return div.innerHTML;
    }
  });
}
